import { useEffect, useState } from "react";
import {
  Newspaper,
  Plus,
  Loader2,
  Trash2,
  Archive,
  RotateCcw,
  Save,
  CalendarDays,
  Pencil,
  Eye,
  EyeOff,
  ImagePlus,
  X,
} from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const TYPES = [
  { type: "news", label: "Tax News", key: "news_enabled", heading: "Tax News & Updates" },
  { type: "blog", label: "Blog", key: "blog_enabled", heading: "Blog Updates" },
  { type: "event", label: "Events", key: "events_enabled", heading: "Upcoming Events" },
];

const emptyForm = { type: "news", title: "", body: "", event_date: "", image_url: "" };

export default function AdminContent() {
  const [sections, setSections] = useState({ news_enabled: false, blog_enabled: false, events_enabled: false });
  const [savingSections, setSavingSections] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("news");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/settings/sections"), api.get("/admin/content")])
      .then(([s, c]) => {
        setSections(s.data);
        setItems(c.data);
      })
      .catch(() => toast.error("Failed to load content"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const saveSections = async () => {
    setSavingSections(true);
    try {
      await api.put("/admin/settings/sections", {
        news_enabled: !!sections.news_enabled,
        blog_enabled: !!sections.blog_enabled,
        events_enabled: !!sections.events_enabled,
      });
      toast.success("Section visibility saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingSections(false);
    }
  };

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...emptyForm, type: active });
    setOpen(true);
  };

  const openEdit = (it) => {
    setEditingId(it.id);
    setForm({ type: it.type, title: it.title || "", body: it.body || "", event_date: it.event_date || "", image_url: it.image_url || "" });
    setOpen(true);
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/content/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: `${API}/content/image/${data.path}` }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const saveItem = async (e) => {
    e.preventDefault();
    if (!form.title || !form.body) {
      toast.error("Title and body are required.");
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title,
      body: form.body,
      event_date: form.type === "event" ? form.event_date || null : null,
      image_url: form.image_url || null,
    };
    try {
      if (editingId) {
        await api.put(`/admin/content/${editingId}`, payload);
        toast.success("Item updated");
      } else {
        await api.post("/admin/content", { ...payload, type: form.type });
        toast.success("Item published");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.post(`/admin/content/${id}/status`, { status });
      toast.success(status === "archived" ? "Archived" : "Restored");
      load();
    } catch (err) {
      toast.error("Action failed");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/content/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const filtered = items.filter((i) => i.type === active);

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-heading text-2xl tracking-tight font-medium text-slate-900 flex items-center gap-2">
          <Newspaper className="w-6 h-6 text-[#1E3A8A]" /> News, Blog &amp; Events
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Publish updates from the tax office. Enable a section to show it in the top navigation and
          on the landing page.
        </p>
      </div>

      {/* Section visibility */}
      <div className="bg-white border border-slate-200 rounded-md p-6 mb-6">
        <h3 className="font-heading text-lg font-medium text-slate-900 mb-4">Section visibility</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          {TYPES.map((t) => (
            <div key={t.key} className="flex items-center justify-between p-3 bg-slate-50 rounded-md">
              <span className="text-sm font-medium text-slate-700">{t.label}</span>
              <Switch
                checked={!!sections[t.key]}
                onCheckedChange={(v) => setSections((s) => ({ ...s, [t.key]: v }))}
                data-testid={`toggle-${t.type}`}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-4">
          <Button onClick={saveSections} disabled={savingSections} className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" data-testid="save-sections-btn">
            {savingSections ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save visibility
          </Button>
        </div>
      </div>

      {/* Type selector + add */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          {TYPES.map((t) => (
            <button
              key={t.type}
              onClick={() => setActive(t.type)}
              data-testid={`content-tab-${t.type}`}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                active === t.type ? "bg-[#1E3A8A] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
        <Button
          variant="outline"
          onClick={() => setShowPreview((v) => !v)}
          className="border-slate-300"
          data-testid="toggle-preview-btn"
        >
          {showPreview ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
          {showPreview ? "Hide preview" : "Homepage preview"}
        </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openAdd} className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" data-testid="add-content-btn">
              <Plus className="w-4 h-4 mr-2" /> Add {TYPES.find((t) => t.type === active)?.label}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingId ? "Edit" : "New"} {TYPES.find((t) => t.type === form.type)?.label} item
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={saveItem} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="content-title-input" />
              </div>
              <div className="space-y-2">
                <Label>Body</Label>
                <Textarea rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="content-body-input" />
              </div>
              {form.type === "event" && (
                <div className="space-y-2">
                  <Label>Event date</Label>
                  <Input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="content-date-input" />
                </div>
              )}
              <div className="space-y-2">
                <Label>Featured image</Label>
                {form.image_url ? (
                  <div className="relative w-full overflow-hidden rounded-md border border-slate-200">
                    <img src={form.image_url} alt="Preview" className="w-full h-40 object-cover" data-testid="content-image-preview" />
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, image_url: "" }))}
                      className="absolute top-2 right-2 bg-white/90 hover:bg-white rounded-full p-1 shadow-sm"
                      data-testid="content-image-remove"
                    >
                      <X className="w-4 h-4 text-slate-700" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center gap-2 w-full h-24 border border-dashed border-slate-300 rounded-md cursor-pointer text-sm text-slate-500 hover:bg-slate-50 transition-colors" data-testid="content-image-dropzone">
                    {uploading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
                    ) : (
                      <><ImagePlus className="w-4 h-4" /> Upload an image (jpg, png, gif, webp)</>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={uploading}
                      onChange={(e) => uploadImage(e.target.files?.[0])}
                      data-testid="content-image-file"
                    />
                  </label>
                )}
                <Input
                  value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  data-testid="content-image-input"
                  placeholder="…or paste an image URL"
                />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={saving} className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white w-full" data-testid="content-submit-btn">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editingId ? "Save changes" : "Publish"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {showPreview && (() => {
        const meta = TYPES.find((t) => t.type === active);
        const today = new Date().toISOString().slice(0, 10);
        let previewItems = filtered.filter((i) => i.status !== "archived");
        if (active === "event") {
          previewItems = previewItems
            .filter((i) => !i.event_date || i.event_date >= today)
            .sort((a, b) => (a.event_date || "9999").localeCompare(b.event_date || "9999"));
        }
        return (
          <div className="bg-slate-50 border border-slate-200 rounded-md p-6 mb-6" data-testid="homepage-preview-panel">
            <div className="flex items-center gap-2 mb-1">
              <Eye className="w-4 h-4 text-[#1E3A8A]" />
              <span className="text-xs uppercase tracking-wide text-slate-500 font-medium">Homepage preview</span>
            </div>
            <h3 className="font-heading text-xl font-medium text-slate-900 mb-4">{meta?.heading || meta?.label}</h3>
            {previewItems.length === 0 ? (
              <p className="text-sm text-slate-500">No published items to show{active === "event" ? " (past events are auto-hidden)" : ""}.</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="homepage-preview-grid">
                {previewItems.map((it) => (
                  <div key={it.id} className="bg-white border border-slate-200 rounded-md overflow-hidden">
                    {it.image_url && <img src={it.image_url} alt="" className="w-full h-32 object-cover" />}
                    <div className="p-4">
                      {active === "event" && it.event_date && (
                        <p className="text-xs text-[#1E3A8A] font-medium mb-1">
                          {new Date(it.event_date).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
                        </p>
                      )}
                      <p className="font-medium text-slate-900">{it.title}</p>
                      <p className="text-sm text-slate-500 mt-1 line-clamp-3 whitespace-pre-line">{it.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-16 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-md p-16 text-center bg-white" data-testid="content-empty">
          <p className="text-slate-500">No items yet. Add one to publish it.</p>
        </div>
      ) : (
        <div className="grid gap-3" data-testid="content-list">
          {filtered.map((it) => (
            <div key={it.id} className="bg-white border border-slate-200 rounded-md p-5 flex items-start justify-between gap-4" data-testid={`content-row-${it.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-slate-900">{it.title}</p>
                  {it.status === "archived" ? (
                    <Badge className="bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-100">Archived</Badge>
                  ) : (
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50">Published</Badge>
                  )}
                  {it.event_date && (
                    <span className="inline-flex items-center gap-1 text-xs text-[#1E3A8A]">
                      <CalendarDays className="w-3 h-3" /> {new Date(it.event_date).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-500 mt-1 line-clamp-2 whitespace-pre-line">{it.body}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button variant="outline" size="sm" className="border-slate-300" onClick={() => openEdit(it)} data-testid={`edit-content-${it.id}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                {it.status === "archived" ? (
                  <Button variant="outline" size="sm" className="border-slate-300" onClick={() => setStatus(it.id, "published")} data-testid={`restore-content-${it.id}`}>
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" className="border-slate-300" onClick={() => setStatus(it.id, "archived")} data-testid={`archive-content-${it.id}`}>
                    <Archive className="w-4 h-4" />
                  </Button>
                )}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" className="border-slate-300 text-red-700 hover:bg-red-50" data-testid={`delete-content-${it.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete "{it.title}"?</AlertDialogTitle>
                      <AlertDialogDescription>This permanently removes the item. This cannot be undone.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(it.id)} className="bg-red-700 hover:bg-red-800" data-testid={`confirm-delete-content-${it.id}`}>
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
